import React, { Component } from 'react';

import Dropdown from '@commons/Dropdown';

import type { RankingOrderItem } from '@components/RankingOrder/RankingOrder';

import { isRankingOrderAccessibilityEnabled } from '@services/featureFlags';
import { replaceVariables } from '@services/translations';

import stripHtml from '@utils/stripHtml';

import { EMPTY_TEXT, EMPTY_VALUE } from './DropdownLayout';
import styles from './DropdownWrap.scss';
import { translations } from './constants';

export const TEST_NAME_DROPDOWN_WRAP = 'DROPDOWN_WRAP';

interface DropdownWrapProps {
  id: string;
  optionsLength: number;
  selected: RankingOrderItem;
  setAriaLiveMessage: (text: string, position: number | null) => void;
  setAriaLiveMessageForLiveRegion: (message: string) => void;
  text: string;
  updatePositions: (id: string, position: number | null, defaultPosition: number) => void;
  label?: string | null;
  defaultPosition: number;
}

export default class DropdownWrap extends Component<DropdownWrapProps> {
  static defaultProps = {
    label: null
  };

  static getOptions(length) {
    const options = Array(length)
      .fill()
      .reduce(
        (acc, item, idx) => {
          acc.push({
            text: `${idx + 1}`,
            id: `${idx + 1}` // increment to match Dropdown logic
          });
          return acc;
        },
        [
          {
            text: EMPTY_TEXT,
            id: EMPTY_VALUE
          }
        ]
      );

    return options;
  }

  constructor(props) {
    super(props);
  }

  /* I have no good idea of how to test it at the time of writing the code */
  /* istanbul ignore next */

  onChange = (newVal) => {
    const {
      props: {
        id,
        text,
        setAriaLiveMessage,
        updatePositions,
        optionsLength,
        setAriaLiveMessageForLiveRegion,
        defaultPosition
      }
    } = this;

    const isReset = newVal === EMPTY_VALUE;
    const positionNewVal = isReset ? null : Number(newVal) - 1; // zero based
    const ariaNewVal = isReset ? null : Number(newVal);
    updatePositions(id, positionNewVal, defaultPosition);
    if (isRankingOrderAccessibilityEnabled()) {
      if (isReset) {
        this.onDropDownReset();
      } else {
        // Added timeout so that the reordering message should be given immediate preference then the item name announcement.
        setTimeout(() => {
          setAriaLiveMessageForLiveRegion(
            replaceVariables(translations().DROPDOWN_REORDERING_DONE, {
              ITEM: stripHtml(text),
              POSITION: ariaNewVal,
              TOTAL: optionsLength
            })
          );
        }, 0);
      }
    } else {
      setAriaLiveMessage(text, ariaNewVal);
    }
  };

  onDropDownOpen = (currentFocusedItem) => {
    const {
      props: { text, setAriaLiveMessageForLiveRegion }
    } = this;

    if (currentFocusedItem === 0) {
      setAriaLiveMessageForLiveRegion(
        replaceVariables(translations().DROPDOWN_REORDERING_STARTED_UNSET, {
          ITEM: stripHtml(text)
        })
      );
    } else {
      setAriaLiveMessageForLiveRegion(
        replaceVariables(translations().DROPDOWN_REORDERING_STARTED, {
          ITEM: stripHtml(text),
          POSITION: currentFocusedItem
        })
      );
    }
  };

  onDropDownClose = () => {
    const { setAriaLiveMessageForLiveRegion } = this.props;
    setAriaLiveMessageForLiveRegion(translations().DROPDOWN_REORDERING_CANCELED);
  };

  onDropDownReset = () => {
    const {
      props: { text, setAriaLiveMessageForLiveRegion }
    } = this;
    setAriaLiveMessageForLiveRegion(
      replaceVariables(translations().DROPDOWN_REORDERING_DONE_UNSET, {
        ITEM: stripHtml(text)
      })
    );
  };

  render() {
    const {
      props: { label, selected, optionsLength },
      onChange,
      onDropDownOpen,
      onDropDownClose
    } = this;

    const options = DropdownWrap.getOptions(optionsLength);

    return (
      <div data-test-name={TEST_NAME_DROPDOWN_WRAP}>
        <Dropdown
          isDropdownLabelInactive
          dropdownList={options}
          selected={selected}
          onChange={onChange}
          containerStyles={styles.dropdownContainer}
          selectorStyles={styles.dropdownSelector}
          label={label}
          modalHeaderStyles={styles.modalHeaderStyles}
          onDropDownOpen={onDropDownOpen}
          onDropDownClose={onDropDownClose}
        />
      </div>
    );
  }
}
